/**
 * Pure SVG paint helpers for frozen graph edges (host-resolved pathD + caps + labels).
 * Ported from Containd PassiveGraphEdgesWithAdornmentsView / graphCapPaths — no RN deps.
 */
import type {
  SnapshotEdgeCapType,
  SnapshotEdgeDashType,
  SnapshotGraphEdge,
  SnapshotCanvasViewSettings,
} from '@containd/shared';

export interface Vec2 {
  x: number;
  y: number;
}

const ARROW_LENGTH = 18;
const ARROW_HALF_WIDTH = 7;
const CIRCLE_RADIUS = 7;
const DIAMOND_HALF_LENGTH = 11;
const DIAMOND_HALF_WIDTH = 7;
const DEFAULT_LABEL_SIZE = 9;

function perp(v: Vec2): Vec2 {
  return { x: -v.y, y: v.x };
}
function add(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x + b.x, y: a.y + b.y };
}
function scale(v: Vec2, s: number): Vec2 {
  return { x: v.x * s, y: v.y * s };
}
function fmt(v: Vec2): string {
  return `${v.x.toFixed(2)} ${v.y.toFixed(2)}`;
}

function arrowFilledPath(tip: Vec2, inward: Vec2, s: number): string {
  const base = add(tip, scale(inward, ARROW_LENGTH * s));
  const p = perp(inward);
  const left = add(base, scale(p, ARROW_HALF_WIDTH * s));
  const right = add(base, scale(p, -ARROW_HALF_WIDTH * s));
  return `M ${fmt(tip)} L ${fmt(left)} L ${fmt(right)} Z`;
}

function arrowOpenPath(tip: Vec2, inward: Vec2, s: number): string {
  const base = add(tip, scale(inward, ARROW_LENGTH * s));
  const p = perp(inward);
  const left = add(base, scale(p, ARROW_HALF_WIDTH * s));
  const right = add(base, scale(p, -ARROW_HALF_WIDTH * s));
  return `M ${fmt(left)} L ${fmt(tip)} L ${fmt(right)}`;
}

function openArrowLineStopOffset(strokeWidth: number, s: number): number {
  const insetFromTip = Math.max(4, strokeWidth * 1.25);
  return Math.max(0, ARROW_LENGTH * s - insetFromTip);
}

function circleCenter(tip: Vec2, inward: Vec2, s: number): Vec2 {
  return add(tip, scale(inward, CIRCLE_RADIUS * s));
}

function diamondPath(tip: Vec2, inward: Vec2, s: number): string {
  const p = perp(inward);
  const mid = add(tip, scale(inward, DIAMOND_HALF_LENGTH * s));
  const left = add(mid, scale(p, DIAMOND_HALF_WIDTH * s));
  const right = add(mid, scale(p, -DIAMOND_HALF_WIDTH * s));
  const far = add(tip, scale(inward, DIAMOND_HALF_LENGTH * 2 * s));
  return `M ${fmt(tip)} L ${fmt(left)} L ${fmt(far)} L ${fmt(right)} Z`;
}

export interface CapGeometry {
  pathD?: string;
  circleCenter?: Vec2;
  circleRadius?: number;
  fill: boolean;
  closedHollow?: boolean;
  lineStopOffset?: number;
}

export function buildCapGeometry(
  tip: Vec2,
  inward: Vec2,
  capType: SnapshotEdgeCapType | undefined,
  strokeWidth: number = 2,
): CapGeometry | null {
  const type = capType ?? 'none';
  const s = Math.sqrt(Math.max(1, strokeWidth) / 2);
  switch (type) {
    case 'none':
      return null;
    case 'arrow-filled':
      return {
        pathD: arrowFilledPath(tip, inward, s),
        fill: true,
        lineStopOffset: ARROW_LENGTH * s,
      };
    case 'arrow-open':
      return {
        pathD: arrowOpenPath(tip, inward, s),
        fill: false,
        lineStopOffset: openArrowLineStopOffset(strokeWidth, s),
      };
    case 'circle-filled':
      return {
        circleCenter: circleCenter(tip, inward, s),
        circleRadius: CIRCLE_RADIUS * s,
        fill: true,
      };
    case 'circle-open':
      return {
        circleCenter: circleCenter(tip, inward, s),
        circleRadius: CIRCLE_RADIUS * s,
        fill: false,
        closedHollow: true,
      };
    case 'diamond-filled':
      return { pathD: diamondPath(tip, inward, s), fill: true };
    case 'diamond-open':
      return {
        pathD: diamondPath(tip, inward, s),
        fill: false,
        closedHollow: true,
      };
    default:
      return null;
  }
}

export function dashArrayForStyle(
  dashType: SnapshotEdgeDashType | undefined,
  strokeWidth: number,
): string | undefined {
  switch (dashType) {
    case 'dashed':
      return `${strokeWidth * 4} ${strokeWidth * 2}`;
    case 'dotted':
      return `${strokeWidth} ${strokeWidth * 2}`;
    default:
      return undefined;
  }
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  if (full.length !== 6) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

export function labelPillFill(labelColor: string): string {
  const rgb = hexToRgb(labelColor);
  if (!rgb) return 'rgba(0,0,0,0.65)';
  const luma = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luma > 0.5 ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.88)';
}

export function sampleBezier(
  x0: number,
  y0: number,
  cx1: number,
  cy1: number,
  cx2: number,
  cy2: number,
  x3: number,
  y3: number,
  t: number,
): Vec2 {
  const mt = 1 - t;
  return {
    x:
      mt * mt * mt * x0 +
      3 * mt * mt * t * cx1 +
      3 * mt * t * t * cx2 +
      t * t * t * x3,
    y:
      mt * mt * mt * y0 +
      3 * mt * mt * t * cy1 +
      3 * mt * t * t * cy2 +
      t * t * t * y3,
  };
}

export function parsePathTangents(
  pathD: string,
  fallbackStart: Vec2,
  fallbackEnd: Vec2,
): { startInward: Vec2; endInward: Vec2 } {
  const tokens = pathD.trim().split(/\s+|(?=[MLC])/);
  const pts: Vec2[] = [];
  let cx = 0;
  let cy = 0;
  let i = 0;

  while (i < tokens.length) {
    const cmd = tokens[i];
    i += 1;
    if (cmd === 'M') {
      cx = parseFloat(tokens[i]!);
      cy = parseFloat(tokens[i + 1]!);
      i += 2;
      pts.push({ x: cx, y: cy });
    } else if (cmd === 'L') {
      const nx = parseFloat(tokens[i]!);
      const ny = parseFloat(tokens[i + 1]!);
      i += 2;
      pts.push({ x: nx, y: ny });
      cx = nx;
      cy = ny;
    } else if (cmd === 'C') {
      const c1x = parseFloat(tokens[i]!);
      const c1y = parseFloat(tokens[i + 1]!);
      const c2x = parseFloat(tokens[i + 2]!);
      const c2y = parseFloat(tokens[i + 3]!);
      const ex = parseFloat(tokens[i + 4]!);
      const ey = parseFloat(tokens[i + 5]!);
      i += 6;
      pts.push({ x: c1x, y: c1y });
      pts.push({ x: c2x, y: c2y });
      pts.push({ x: ex, y: ey });
      cx = ex;
      cy = ey;
    }
  }

  function normalize(dx: number, dy: number, fallback: Vec2): Vec2 {
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 0.001) return fallback;
    return { x: dx / len, y: dy / len };
  }

  const startInward =
    pts.length >= 2
      ? normalize(pts[1]!.x - pts[0]!.x, pts[1]!.y - pts[0]!.y, fallbackStart)
      : fallbackStart;

  const n = pts.length;
  const endInward =
    n >= 2
      ? normalize(
          pts[n - 2]!.x - pts[n - 1]!.x,
          pts[n - 2]!.y - pts[n - 1]!.y,
          fallbackEnd,
        )
      : fallbackEnd;

  return { startInward, endInward };
}

export function offsetPathEndpoints(
  pathD: string,
  srcOffset: number,
  srcDir: Vec2,
  tgtOffset: number,
  tgtDir: Vec2,
): string {
  if (srcOffset <= 0 && tgtOffset <= 0) return pathD;
  let out = pathD;
  if (srcOffset > 0) {
    out = out.replace(
      /^M\s*([\d.-]+)\s+([\d.-]+)/,
      (_m, xs: string, ys: string) => {
        const x = parseFloat(xs) + srcDir.x * srcOffset;
        const y = parseFloat(ys) + srcDir.y * srcOffset;
        return `M ${x.toFixed(2)} ${y.toFixed(2)}`;
      },
    );
  }
  if (tgtOffset > 0) {
    out = out.replace(
      /([\d.-]+)\s+([\d.-]+)\s*$/,
      (_m, xs: string, ys: string) => {
        const x = parseFloat(xs) + tgtDir.x * tgtOffset;
        const y = parseFloat(ys) + tgtDir.y * tgtOffset;
        return `${x.toFixed(2)} ${y.toFixed(2)}`;
      },
    );
  }
  return out;
}

export function pathEndpoints(pathD: string): {
  source: Vec2;
  target: Vec2;
} {
  let source = { x: 0, y: 0 };
  let target = { x: 0, y: 0 };
  const mStart = pathD.match(/^M\s*([\d.-]+)\s+([\d.-]+)/);
  if (mStart) {
    source = { x: parseFloat(mStart[1]!), y: parseFloat(mStart[2]!) };
  }
  const mBezierEnd = pathD.match(/C[\s\d.-]+?([\d.-]+)\s+([\d.-]+)\s*$/);
  if (mBezierEnd) {
    target = { x: parseFloat(mBezierEnd[1]!), y: parseFloat(mBezierEnd[2]!) };
  } else {
    const mLineEnd = pathD.match(/L\s*([\d.-]+)\s+([\d.-]+)\s*$/);
    if (mLineEnd) {
      target = { x: parseFloat(mLineEnd[1]!), y: parseFloat(mLineEnd[2]!) };
    }
  }
  return { source, target };
}

export interface EdgeLabelPaint {
  text: string;
  t: number;
  placement: 'start' | 'mid' | 'end';
}

export function labelsForFrozenEdge(
  edge: SnapshotGraphEdge,
  settings: SnapshotCanvasViewSettings,
): EdgeLabelPaint[] {
  const out: EdgeLabelPaint[] = [];
  if (settings.showGraphStartLabels !== false && edge.label?.trim()) {
    out.push({ text: edge.label.trim(), t: 0.05, placement: 'start' });
  }
  if (settings.showGraphMidLabels !== false && edge.midLabel?.trim()) {
    out.push({ text: edge.midLabel.trim(), t: 0.5, placement: 'mid' });
  }
  if (settings.showGraphEndLabels !== false && edge.endLabel?.trim()) {
    out.push({ text: edge.endLabel.trim(), t: 0.95, placement: 'end' });
  }
  return out;
}

export function labelPositionOnPath(
  pathD: string,
  source: Vec2,
  target: Vec2,
  t: number,
): Vec2 {
  const bezierMatch = pathD.match(
    /M\s*([\d.-]+)\s+([\d.-]+)\s+C\s*([\d.-]+)\s+([\d.-]+)\s+([\d.-]+)\s+([\d.-]+)\s+([\d.-]+)\s+([\d.-]+)/,
  );
  if (bezierMatch) {
    return sampleBezier(
      parseFloat(bezierMatch[1]!),
      parseFloat(bezierMatch[2]!),
      parseFloat(bezierMatch[3]!),
      parseFloat(bezierMatch[4]!),
      parseFloat(bezierMatch[5]!),
      parseFloat(bezierMatch[6]!),
      parseFloat(bezierMatch[7]!),
      parseFloat(bezierMatch[8]!),
      t,
    );
  }
  return {
    x: source.x + (target.x - source.x) * t,
    y: source.y + (target.y - source.y) * t,
  };
}

export const GRAPH_EDGE_LABEL_SIZE = DEFAULT_LABEL_SIZE;

/** Match Containd edge notations (sans — not platform SVG serif default). */
export const GRAPH_EDGE_LABEL_FONT_FAMILY =
  "'Source Sans 3', system-ui, sans-serif";

/** Default Containd caps when freeze omitted them (older packages). */
export function resolvedCaps(edge: SnapshotGraphEdge): {
  startCap: SnapshotEdgeCapType;
  endCap: SnapshotEdgeCapType;
} {
  return {
    startCap: edge.startCap ?? 'none',
    endCap: edge.endCap ?? 'arrow-filled',
  };
}
